import { i18n } from '@core/config/i18n.installer.js'
import { readSessionExpiry, SESSION_EXPIRY_KEY } from '@shared/utils/session-token.utils.js'
import ToastEventBus from 'primevue/toasteventbus'
import { ref, watch } from 'vue'

// Reactive view of the current session expiry (ms epoch, null when unknown).
// Consumed by UI that displays session lifetime (e.g. the header countdown).
export const sessionExpiresAtMs = ref(null)

const DEFAULT_WARNING_LEAD_MINUTES = 5

function resolveWarningLeadMinutes() {
  const configured = Number(import.meta.env.VITE_SESSION_WARNING_LEAD_MINUTES)
  return Number.isFinite(configured) && configured > 0 ? configured : DEFAULT_WARNING_LEAD_MINUTES
}

// How long before the token expiry the renewal cycle kicks in (silent renew
// for active users, warning toast for idle ones). Tunable per environment;
// the session lifetime itself is the backend's JWT_EXPIRES_IN (default 1h).
export const WARNING_LEAD_MS = resolveWarningLeadMinutes() * 60 * 1000

// Below this remaining lifetime a warning is pointless: the session-expired
// flow (refresh interceptor) is about to take over anyway.
export const MIN_WARNING_DELAY_MS = 10_000

// A user who interacted this recently gets a silent renewal instead of the
// warning toast: active work should never be interrupted by session expiry.
export const ACTIVITY_WINDOW_MS = 5 * 60 * 1000

// If a silent renewal did not move the expiry (e.g. persistence failed),
// falling back to the toast beats renewing in a loop.
export const SILENT_RENEW_MIN_INTERVAL_MS = 60_000

let started = false
let authStore = null
let warningTimer = null
let warningMessage = null
let stopTokenWatch = null
let lastActivityAt = 0
let lastSilentRenewAt = 0
// Expiry timestamp the user already acted upon (clicked renew or dismissed
// a failed renewal). Prevents the warning from re-appearing on every
// focus/visibility resync for the same dying token.
let acknowledgedExpiryMs = null

function recordActivity() {
  lastActivityAt = Date.now()
}

function isUserActive() {
  return Date.now() - lastActivityAt <= ACTIVITY_WINDOW_MS
}

function clearWarningTimer() {
  if (warningTimer) {
    clearTimeout(warningTimer)
    warningTimer = null
  }
}

function dismissWarning() {
  if (warningMessage) {
    ToastEventBus.emit('remove', { message: warningMessage, type: 'close' })
    warningMessage = null
  }
}

function showWarning() {
  if (warningMessage)
    return

  if (isUserActive() && Date.now() - lastSilentRenewAt >= SILENT_RENEW_MIN_INTERVAL_MS) {
    renewSilently()
    return
  }

  showWarningToast()
}

function showWarningToast() {
  if (warningMessage)
    return

  // No warning outside an authenticated context (e.g. the login page) and
  // none for a token the user already acted upon.
  if (!authStore?.isAuthenticated)
    return
  if (acknowledgedExpiryMs !== null && readSessionExpiry() === acknowledgedExpiryMs)
    return

  warningMessage = {
    severity: 'warning',
    summary: i18n.global.t('session.expiry-warning.title'),
    detail: i18n.global.t('session.expiry-warning.detail'),
    action: {
      label: i18n.global.t('session.expiry-warning.action'),
      onClick: renewSession,
    },
  }

  ToastEventBus.emit('add', warningMessage)
}

async function renewSilently() {
  lastSilentRenewAt = Date.now()
  try {
    await authStore.refreshTokens()
  }
  catch {
    // Silent renewal failed (e.g. B2C refresh ceiling): surface the warning
    // so the user still gets the manual action before the session dies.
    showWarningToast()
  }
}

async function renewSession(close) {
  // Close on click, immediately: the user asked the toast to go away. The
  // renewal continues in the background; success re-schedules via the token
  // watcher, failure ends in the explicit session-expired flow on the next
  // request. Either way this warning is consumed for the current token.
  acknowledgedExpiryMs = readSessionExpiry()
  warningMessage = null
  close()

  try {
    await authStore.refreshTokens()
  }
  catch {
    // Renewal impossible (e.g. B2C refresh ceiling): the next 401 goes
    // through the session-expired flow, which informs the user explicitly.
  }
}

function scheduleWarning(expiryMs) {
  clearWarningTimer()

  if (!expiryMs)
    return

  const remaining = expiryMs - Date.now()
  if (remaining <= MIN_WARNING_DELAY_MS)
    return

  warningTimer = setTimeout(showWarning, Math.max(0, remaining - WARNING_LEAD_MS))
}

// Single re-sync point: reads the persisted expiry and re-schedules.
// Covers page load, token renewal (any tab), tab refocus after background
// timer throttling, and logout in another tab.
function resyncFromPersistedExpiry() {
  const expiry = readSessionExpiry()
  sessionExpiresAtMs.value = expiry

  if (!expiry || expiry - Date.now() > WARNING_LEAD_MS)
    dismissWarning()

  scheduleWarning(expiry)
}

function handleStorageEvent(event) {
  if (event.key === null || event.key === SESSION_EXPIRY_KEY)
    resyncFromPersistedExpiry()
}

export function startSessionExpiryWarning({ authStore: store }) {
  if (started)
    return
  started = true
  authStore = store

  stopTokenWatch = watch(() => store.accessToken, (token) => {
    if (token) {
      resyncFromPersistedExpiry()
    }
    else {
      sessionExpiresAtMs.value = null
      clearWarningTimer()
      dismissWarning()
    }
  })

  document.addEventListener('visibilitychange', resyncFromPersistedExpiry)
  window.addEventListener('focus', resyncFromPersistedExpiry)
  window.addEventListener('storage', handleStorageEvent)
  document.addEventListener('keydown', recordActivity, { passive: true })
  document.addEventListener('pointerdown', recordActivity, { passive: true })
  window.addEventListener('wheel', recordActivity, { passive: true })

  resyncFromPersistedExpiry()
}

export function resetSessionExpiryWarningForTests() {
  clearWarningTimer()
  warningMessage = null
  authStore = null
  started = false
  sessionExpiresAtMs.value = null
  lastActivityAt = 0
  lastSilentRenewAt = 0
  acknowledgedExpiryMs = null
  stopTokenWatch?.()
  stopTokenWatch = null
  document.removeEventListener('visibilitychange', resyncFromPersistedExpiry)
  window.removeEventListener('focus', resyncFromPersistedExpiry)
  window.removeEventListener('storage', handleStorageEvent)
  document.removeEventListener('keydown', recordActivity)
  document.removeEventListener('pointerdown', recordActivity)
  window.removeEventListener('wheel', recordActivity)
}
